/* eslint-disable indent */
import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import cookies from 'cookiesjs'
import store from './index'
import formInterceptors from 'utils/formAxiosInterceptors'
import qs from 'qs'

if (process.env.NODE_ENV === 'production') {
  cookies.domain = location.hostname
  // cookies.secure = false
}

/**
 * Typical errors when requesting api
 * => requiring user to log back in
 * (e.g. token became useless so no API call possible)
 * @type {Array}
 */
const errorRegexes = [
  /token \w+ expired/i, // Token has expired
  /token \w+ invalid$/i, // Token is invalid
  /token [\w\s]+ verified/i, // Token Signature could not be verified.
  /token [\w\s]+ blacklisted$/i,
  /wrong [\w\s]+ segments$/i, // Wrong number of segments
  /.+timestamp cannot be in the future/i,
  /invalid token/i,
  /Could not decode token/i
]

Vue.use(VueAxios, axios)
Vue.axios.defaults.baseURL = `${__API_URL__}/api`
Vue.axios.defaults.timeout = 300000
// 300 seconds
// Vue.axios.defaults.headers.common['Authorization'] = `Bearer ${cookies('token')}`

// interceptors
Vue.axios.interceptors.request.use((config) => {
  if (cookies('accountType') !== 'EMPLOYEE')
    setTimeout(() => store.commit('LOGOUT', true), 0)
  config.headers['Authorization'] = `Bearer ${cookies('token')}`

  // X-Socket-ID lets the backend exclude this client's socket from broadcasts
  // it dispatches in response to this request (broadcast()->toOthers()).
  // Used to avoid double-fetching when the originator already refreshes
  // locally via the action's .then() handler.
  if (window.Echo && typeof window.Echo.socketId === 'function') {
    const socketId = window.Echo.socketId()
    if (socketId) {
      config.headers['X-Socket-ID'] = socketId
    }
  }

  return config
})

Vue.axios.interceptors.response.use((response) => {
  // console.info("API RAW Response:", response)
  const uid = store.state.resourceForm.uid

  formInterceptors.success(response, store, uid)
  return response
}, (error) => {
  // TODO: handle timeout here (error.code == 'ECONNABORTED', error.message ~= /timeout/)
  console.log(
    "timeout conditions:",
    error.code === 'ECONNABORTED',
    /timeout/i.test(error.message)
  )
  let logout = false
  // debugger
  // searching for any token error
  !_.isNil(error.response) && _.each(errorRegexes, regex => {
    // API returns something yet in case of error
    // so actual error message is in `error.response.data`
    if (regex.test(error.response.data.message)) {
      logout = true
      // set Application state as logged out
      // debugger
      setTimeout(() => store.commit('LOGOUT', true), 0)
      // no point on continuing
      // exiting the loop now (see lodash#each)
      return false
    }
  })

  if (!logout && !_.isNil(error.response)) {
    const uid = store.state.resourceForm.uid
    /* const uid = store.state.resourceForm.uid,
     mutationName = _.isEmpty(uid) ? 'FORM_ERROR' : `${uid}/FORM_ERROR`
     store.commit(mutationName, error.response.data) */
    formInterceptors.error(error.response, store, uid)
  }

  if (!_.isNil(error.response) && error.response.status >= 500) {
    let formattedErrors = _.map(error.response.data.errors, (error) => {
        return `<p>${error}</p>`
    }).join('') || error.response.data.message

    $('body').pgNotification({
        style: "bar",
        title: error.response.data.message || error.response.statusText,
        type: 'success',
        message: formattedErrors,
        timeout: 7000,
    }).show()
  }

  return Promise.reject(error)
})

/**
 * API functions collection Object
 * @type {Object}
 */
const api = {
  /**
   * login function
   * @param  {Object} data  email, password, rememberEmail
   * @return {Object}       raw axios response object
   */
  login(data) {
    return Vue.axios.post('auth/agency_login', data).then(response => {
      let token = response.data.token
      cookies({ token })
      return response
    })
  },

  /**
   * logout function
   * @return {Promise}  resolved promise (=true)
   */
  logout() {
    cookies({ token: null })
    return Promise.resolve(true)
  },

  /**
   * get Customers data (index endpoint)
   * @param  {Number} idxPage   one-based page index
   * @return {Promise}          raw axios promise
   */
  getCustomers(idxPage, otherParams = {}, cancelToken) {
    otherParams = _.mapValues(
      otherParams,
      (param) => _.isBoolean(param) ? Number(param) : param
    )
    const params = {
      page: idxPage,
      ...otherParams
    }
    return Vue.axios.get('customers', {
      params,
      cancelToken
    })
  },

  getProspects() {
    const params = { validated: 0, paginate: 0, eager_loading: 0 }

    return Vue.axios.get('customers', { params })
  },

  getCustomer(id) {
      const params = { with: 'buyers.email_suppression;activity_log.user' }
    return Vue.axios.get(`customers/${id}`, { params })
  },

  getMainAddress(customerId) {
    return Vue.axios.get(`customers/${customerId}/addresses/main`)
  },

  getBillingAddress(customerId) {
    return Vue.axios.get(`customers/${customerId}/addresses/corresponding`)
  },

  getAddresses(customerId) {
    return Vue.axios.get(`customers/${customerId}/addresses`)
  },

  getRepresentatives(customerId) {
    return Vue.axios.get(`customers/${customerId}/representatives`)
  },

  mainifyRepresentative(customerId, representativeId, model) {
    return Vue.axios.put(`customers/${customerId}/representatives/${representativeId}/mainify`, model)
  },

  getBrands(customerId) {
    return Vue.axios.get(`customers/${customerId}/brands`)
  },

  getMailWithdrawalProxies(contractId) {
    return Vue.axios.get(`contracts/${contractId}/mail_withdrawal_proxies`)
  },

  addMailWithdrawalProxy(contractId, model) {
    return Vue.axios.post(`contracts/${contractId}/mail_withdrawal_proxies`, model)
  },

  removeMailWithdrawalProxy(contractId, mailWithdrawalProxiesId) {
    return Vue.axios.delete(`contracts/${contractId}/mail_withdrawal_proxies/${mailWithdrawalProxiesId}`)
  },

  getOrders(customerId, params) {
    return Vue.axios.get(`customers/${customerId}/orders`, { params })
  },

  getContracts(customerId, params) {
    return Vue.axios.get(`customers/${customerId}/contracts`, { params })
  },

  syncCustomerDigitalContracts(customerId, contractId) {
    return Vue.axios.get(`customers/${customerId}/contracts/${contractId}/sync_digital_contracts`)
  },

  getOrder(customerId, orderId) {
    return Vue.axios.get(`customers/${customerId}/orders/${orderId}`)
  },

  getActiveMailForwardingAddress(contractId, contractOptionId) {
    return Vue.axios.get(`contracts/${contractId}/options/${contractOptionId}/addresses/active`)
  },

  getDefaultMailForwardingAddress(contractId, contractOptionId) {
    return Vue.axios.get(`contracts/${contractId}/options/${contractOptionId}/addresses/default`)
  },

  getFutureDefaultMailForwardingAddress(contractId, contractOptionId) {
    return Vue.axios.get(`contracts/${contractId}/options/${contractOptionId}/addresses/future_default`)
  },

  getAllMailForwardingAddresses(contractId, contractOptionId) {
    return Vue.axios.get(`contracts/${contractId}/options/${contractOptionId}/addresses`)
  },

  getIntermediaryBusiness(customerId) {
    return Vue.axios.get(`customers/${customerId}/intermediary_business`)
  },

  removeIntermediaryBusiness(customerId) {
    return Vue.axios.delete(`customers/${customerId}/intermediary_business`)
  },

  addIntermediaryBusiness(customerId, model) {
    const data = { intermediary_business_id: model.id }

    return Vue.axios.post(`customers/${customerId}/intermediary_business`, data)
  },

  getCustomerSapOrganizations(customerId, params) {
    return Vue.axios.get(`customers/${customerId}/sap_organizations`, {params})
  },

  getCustomerActivityLog(customerId, params) {
    return Vue.axios.get(`customers/${customerId}/activity_log`, { params })
  },

  // Hotline contacts
  fetchHotlineContacts(contractId, contractOptionId) {
    return Vue.axios.get(`contracts/${contractId}/options/${contractOptionId}/hotline_contacts`)
  },

  addHotlineContact(contractId, contractOptionId, model) {
    return Vue.axios.post(`contracts/${contractId}/options/${contractOptionId}/hotline_contacts`, model)
  },

  updateHotlineContact(contractId, contractOptionId, hotlineContactId, model) {
    return Vue.axios.put(`contracts/${contractId}/options/${contractOptionId}/hotline_contacts/${hotlineContactId}`, model)
  },

  removeHotlineContact(contractId, contractOptionId, hotlineContactId) {
    return Vue.axios.delete(`contracts/${contractId}/options/${contractOptionId}/hotline_contacts/${hotlineContactId}`)
  },


  /**
   * get logged user data (to check for API connection state)
   * @return {Promise} raw axios response promise
   */
  getLoggedUser() {
    return Vue.axios.get('users/me', { params: { 'with': 'groups' } })
  },

  /**
   * get logged user agencies for caching purposes
   * since a lot of data will depend on it
   * @return {Promise} raw axios response promise
   */
  getUserAgencies(userId) {
    return Vue.axios.get(`users/${userId}/agencies`)
  },

  getUserOrganizations(userId) {
    return Vue.axios.get(`users/${userId}/organizations`)
  },

  getUser(userId) {
    return Vue.axios.get(`users/${userId}`)
  },

  fetchAgencies() {
    return Vue.axios.get('agencies')
  },

  fetchOrganizations() {
    return Vue.axios.get('organizations')
  },
  
  fetchSapPromoCodes() {
    return Vue.axios.get('sap_promo_codes')
  },

  updateCustomerComments(customerId, comments) {
    return Vue.axios.put(`customers/${customerId}/update_comments`, {
      comments
    })
  },

  updateCustomer(customerId, customer) {
    return Vue.axios.put(`customers/${customerId}`, customer)
  },

  updateDefaultBuyer(customerId, defaultBuyerId) {
    return Vue.axios.put(`customers/${customerId}/update_default_buyer`, {
      default_buyer_id: defaultBuyerId
    })
  },

  updateRepresentative(customerId, model) {
    const representativeId = model.representative_id

    return Vue.axios.put(`customers/${customerId}/representatives/${representativeId}`, model)
  },

  updateRepresentativeInfo(customerId, model) {
    const representativeId = model.representative_id

    return Vue.axios.put(`customers/${customerId}/representatives/${representativeId}/update_info`, model)
  },

  updateRepresentativeAddress(customerId, model) {
    const representativeId = model.representative_id

    return Vue.axios.put(`customers/${customerId}/representatives/${representativeId}/update_address`, model)
  },

  terminateRepresentative(customerId, representativeId, params = false) {
    return Vue.axios.put(
      `customers/${customerId}/representatives/${representativeId}/terminate`,
      (params) ? params : {}
    )
  },

  fetchContract(contractId) {
    return Vue.axios.get(`contracts/${contractId}`)
  },

  getCustomerContract(customerId, contractId, params) {
    return Vue.axios.get(`customers/${customerId}/contracts/${contractId}`, { params })
  },

  fetchContractInstructions(contractId) {
    return Vue.axios.get(`contracts/${contractId}/edit_instructions`)
  },

  updateContractInstructions(contractId, model) {
    return Vue.axios.put(`contracts/${contractId}/update_instructions`, model)
  },

  cancelContract(contractId) {
    return Vue.axios.put(`contracts/${contractId}/cancel`)
  },

  updateAddress(customerId, addressId, model) {
    return Vue.axios.put(`customers/${customerId}/addresses/${addressId}`, model)
  },

  fetchOptionInstructions(contractId, contractOptionId) {
    return Vue.axios.get(`contracts/${contractId}/options/${contractOptionId}/edit_instructions`)
  },

  updateOptionInstructions(contractId, contractOptionId, model) {
    return Vue.axios.put(
      `contracts/${contractId}/options/${contractOptionId}/update_instructions`,
      model
    )
  },

  storeCustomerErpRecord(customerId) {
    return Vue.axios.post(`customers/${customerId}/store_erp_record`)
  },

  addBrand(customerId, model) {
    return Vue.axios.post(`customers/${customerId}/brands`, model)
  },

  archiveBrand(customerId, brandId) {
    return Vue.axios.put(`customers/${customerId}/brands/${brandId}/archive`)
  },

  removeBrand(customerId, brandId) {
    return Vue.axios.delete(`customers/${customerId}/brands/${brandId}`)
  },

  addConsumption(data) {
    return Vue.axios.post(`consumptions`, data)
  },

  fetchConsumptions(params, cancelToken) {
    return Vue.axios.get(`consumptions`, {
      params,
      cancelToken
    })
  },

  removeConsumption(consumptionId) {
    return Vue.axios.delete(`consumptions/${consumptionId}`)
  },

  fetchCustomers(params) {
    const _params = _.merge(
      {},
      {
        params: {
          paginate: 0,
          eager_loading: 0
        }
      },
      { params }
    )
    return Vue.axios.get('customers', _params)
  },

  fetchOffers(params) {
    return Vue.axios.get('offers', { params })
  },

  getRepresentativeMainificationForm(customerId, representativeId) {
    return Vue.axios.get(`customers/${customerId}/representatives/${representativeId}/mainification_form`)
  },

  fetchContractProxies(contractId) {
    return Vue.axios.get(`contracts/${contractId}/proxies`)
  },

  fetchCatalog(params) {
    return (Vue.axios.get(`offers`, { params }))
  },

  // CART API PART
  fetchCarts(params) {
    return (Vue.axios.get(`carts`, { params }))
  },

  fetchCartById(cartId, params) {
    return (Vue.axios.get(`carts/${cartId}`, { params }))
  },

  createCart(params) {
    return (Vue.axios.post(`carts`, { params }))
  },

  fetchActiveCart(params) {
    return (Vue.axios.get(`carts/active`, {}))
  },

  setCurrentStepForCart(cartId, params) {
    return (Vue.axios.post(`carts/${cartId}/set_current_step`, { params }))
  },

  setOwnerForCart(cartId, params) {
    return (Vue.axios.put(`carts/${cartId}/set_owner`, params))
  },

  getOwnerForCart(cartId) {
    return (Vue.axios.get(`carts/${cartId}/get_owner`))
  },


  passingCustomerForCart(cartId, params) {
    return (Vue.axios.post(`carts/${cartId}/passing_customer`, { params }))
  },

  attachCartToCustomer(cartId, params) {
    return (Vue.axios.post(`carts/${cartId}/attach_customer`, { params }))
  },

  resetCartOwner(cartId, params) {
    return (Vue.axios.post(`carts/${cartId}/reset_owner`, { params }))
  },

  addOfferToCart(cartId, params) {
    return (Vue.axios.post(`carts/${cartId}/add_offer`, params))
  },

  computePriceAgainForCart(cartid, params) {
    return (Vue.axios.get(`carts/${cartId}/recalculate`, { params }))
  },

  changeDomiciliationOffer(cartId, params) {
    return (Vue.axios.post(`carts/${cartId}/change_domiciliation_offer`, { params }))
  },

  cleanCart(cartId, params) {
    return (Vue.axios.post(`carts/${cartId}/clean`, params))
  },

  //HERE
  getPurchaseManifest(cartId, params) {
    return (Vue.axios.get(`carts/${cartId}/purchase_tunnel_manifest`, { params }))
  },

  getPurchaseSteps(cartId, params) {
    return (Vue.axios.get(`carts/${cartId}/purchase_tunnel_steps`, { params }))
  },

  getPurchaseCartSteps(cartId, params) {
    return (Vue.axios.get(`carts/${cartId}/purchase_tunnel_cart_step`, { params }))
  },

  getPurchaseTunnelConnectionStep(cartId, params) {
    return (Vue.axios.get(`carts/${cartId}/purchase_tunnel_connection_step`, { params }))
  },

  getPurchaseInformationStepEditionStatus(cartId, params) {
    return (Vue.axios.get(`carts/${cartId}/information_step_edition_status`, { params }))
  },

  // CART COMPANY
  getCartCompanyIndex(cartId, params) {
    return (Vue.axios.get(`carts/${cartId}/company`, { params }))
  },

  createCartCompany(cartId, params) {
    return (Vue.axios.get(`carts/${cartId}/company/create`, { params }))
  },

  storeCompany(cartId, params) {
    return (Vue.axios.post(`carts/${cartId}/company`, { params }))
  },

  editCartCompany(cartId, params) {
    return (Vue.axios.get(`carts/${cartId}/company/edit`, { params }))
  },

  updateCartCompany(cartId, params) {
    return (Vue.axios.post(`carts/${cartId}/company/update`, { params }))
  },

  delCartCompany(cartId, params) {
    return (Vue.axios.delete(`carts/${cartId}/company`, { params }))
  },

  // CART SUBORDER

  storeSuborders(cartId, params) {
    return (Vue.axios.post(`carts/${cartId}/suborders`, { params }))
  },

  updateSuborders(cartId, offerId, params) {
    return (Vue.axios.post(`carts/${cartId}/suborders/${offerId}/update_offer`, params))
  },

  updateSubordersQuantity(cartId, offerId, params) {
    return (Vue.axios.post(`carts/${cartId}/suborders/${offerId}/update_quantity`, params))
  },

  updateSubordersInformation(cartId, offerId, params) {
    return (Vue.axios.post(`carts/${cartId}/suborders/${offerId}/update_info`, { params }))
  },

  deleteSuborders(cartId, offerId, params) {
    return (Vue.axios.delete(`carts/${cartId}/suborders/${offerId}`, { params }))
  },

  // CART SUBORDER OPTION

  storeSubordersOptions(cartId, offerId, params) {
    return (Vue.axios.post(`carts/${cartId}/suborders/${offerId}/options`, { params }))
  },

  updateSubordersOptions(cartId, offerId, optionId, params) {
    return (Vue.axios.post(`carts/${cartId}/suborders/${offerId}/options/${optionId}`, { params }))
  },

  deleteSubordersOptions(cartId, offerId, params) {
    return (Vue.axios.delete(`carts/${cartId}/suborders/${offerId}/options/${optionId}`, { params }))
  },

  fetchOfferById(offerId) {
    return (Vue.axios.get(`offers/${offerId}`, { offer_id: offerId }))
  },

  getValidationRequests(params) {
    return (Vue.axios.get(`validation_requests`, { params }))
  },

  getValidationRequestsById(validation_requests_id) {
    return (Vue.axios.get(`validation_requests/${validation_requests_id}`, { 
      params: { 
        with: "type;parent_request;child_requests;customer;creating_user;treating_user;customer_documents;validation_requestable" 
      } 
    }))
  },

  getValidationRequestsTreatmentForm(validation_requests_id) {
    return (Vue.axios.get(`validation_requests/${validation_requests_id}/treatment_form`, {}))

  },

  valideValidationRequests(validation_request_id, params) {
    return (Vue.axios.post(`validation_requests/${validation_request_id}/validate`, params))
  },

  refuseValidationRequests(validation_request_id) {
    return (Vue.axios.post(`validation_requests/${validation_request_id}/refuse`, {}))
  },

  searchCustomer(params) {
    return (Vue.axios.post(`customers/search`, params))
  },

  getPaymentMethods(order_id) {
    return (Vue.axios.get(`orders/${order_id.order_id}/payment_methods`, {}))
  },

  attachSapCustomer(order_id, body) {
    return (Vue.axios.post(`orders/${order_id}/attach_sap_customer`, { sap_customer: body }))
  },

  makePayment(params) {
    var tmp = _.assign({ order_id: params.order_id }, params.other)
    return (Vue.axios.post(`orders/${params.order_id}/store_payment`, tmp))
  },

  get_order_with_options(params) {
    return (Vue.axios.get(`orders/${params.order_id}`, {}))
  },

  get_dashboard_stats(params) {
    return (Vue.axios.get(`agencies/${params.agency}/dashboard_stats`))
  },

  // Expected customer documents
  getCustomerExpectedDocuments(customerId, params) {
    return Vue.axios.get(`customers/${customerId}/expected_documents`, { params })
  },
  getValidationRequestExpectedDocuments(validationRequestId, params) {
    return Vue.axios.get(`validation_requests/${validationRequestId}/expected_documents`, { params })
  },

  // Customer documents
  getCustomerDocument(customerDocumentId, params) {
    return Vue.axios.get(`customer_documents/${customerDocumentId}`, { params })
  },
  getCustomerDocumentArchivedVersions(customerDocumentId) {
    return Vue.axios.get(`customer_documents/${customerDocumentId}/archived_versions`)
  },
  validateCustomerDocument(customerDocumentId, params) {
    return Vue.axios.put(`customer_documents/${customerDocumentId}/validate`, params)
  },
  invalidateCustomerDocument(customerDocumentId) {
    return Vue.axios.put(`customer_documents/${customerDocumentId}/invalidate`)
  },
  updateCustomerDocumentExpirationDate(customerDocumentId, params) {
    return Vue.axios.put(`customer_documents/${customerDocumentId}/update_expiration_date`, params)
  },
  // Customer document files
  storeCustomerDocumentFile(customerDocumentId, formData) {
    return Vue.axios.post(`customer_documents/${customerDocumentId}/customer_document_files`, formData)
  },
  deleteCustomerDocumentFile(customerDocumentFileId) {
    return Vue.axios.delete(`customer_document_files/${customerDocumentFileId}`)
  },
  reorderCustomerDocumentFile(params) {
    return Vue.axios.put(`customer_document_files/${params.customer_document_file_id}/reorder`, params.params)
  },
  // Customer internal documents
  getCustomerInternalDocuments(customerId, params) {
    return Vue.axios.get(`customers/${customerId}/customer_internal_documents`, { params })
  },
  getCustomerInternalDocument(customerId, customerInternalDocumentId, params) {
    return Vue.axios.get(`customers/${customerId}/customer_internal_documents/${customerInternalDocumentId}`, { params })
  },
  // Customer internal document files
  getCustomerInternalDocumentFiles(customerInternalDocumentId, params) {
    return Vue.axios.get(`customer_internal_documents/${customerInternalDocumentId}/customer_internal_document_files`, { params })
  },
  storeCustomerInternalDocumentFile(customerId, formData) {
    return Vue.axios.post(`customers/${customerId}/customer_internal_document_files`, formData)
  },
  deleteCustomerInternalDocumentFile(customerInternalDocumentId, customerInternalDocumentFileId) {
    return Vue.axios.delete(`customer_internal_documents/${customerInternalDocumentId}/customer_internal_document_files/${customerInternalDocumentFileId}`)
  },
  reorderCustomerInternalDocumentFile(params) {
    return Vue.axios.put(`customer_internal_documents/${params.customer_internal_document_id}/customer_internal_document_files/${params.customer_internal_document_file_id}/reorder`, params.params)
  },

  fetch_invoices_of_customer(params) {
    return (Vue.axios.get(`/customers/${params.customer_id}/invoices`, params.params))
  },

  get_customers_for_cash(params) {
    return Vue.axios.get('customers', params)
  },

  payments_methods_of(order_id) {
    return Vue.axios.get(`/orders/${order_id}/payment_methods`)
  },

  cancel_authorizations(order_id) {
    return Vue.axios.get(`orders/${order_id}/cancel_authorizations`)
  },

  capture_autorizations(order_id) {
    return Vue.axios.get(`orders/${order_id}/capture_authorizations`)
  },

  validation_requests(params) {
    return Vue.axios.get(`validation_requests`, params)
  },

  // Customer mail scans
  getCustomerMailScans(customerId, params) {
    return Vue.axios.get(`customers/${customerId}/mail_scans`, { params })
  },

  // CASH
  get_customer_cashings(params) {
    let requestParams = {...params, 'orderBy': 'created_at', 'sortedBy': 'desc'}
    return Vue.axios.get(`customer_cashings`, { params: requestParams })
  },

  get_lone_customer_cashings(cashing_id, params) {
    return Vue.axios.get(`customer_cashings/${cashing_id}`, params)
  },

  post_customer_cashings(params) {
    return Vue.axios.post(`customer_cashings`, params)
  },

  del_customer_cashings(cashing_id, params) {
    return Vue.axios.delete(`customer_cashings/${cashing_id}`, params)
  },

  get_customer_cashings_payment_methods(cashing_id, params) {
    return Vue.axios.get(`customer_cashings/${cashing_id}/payment_methods`, params)
  },

  post_customer_cashings_payment_methods(cashing_id, params) {
    return Vue.axios.post(`customer_cashings/${cashing_id}/store_payment`, params)
  },

  cancel_cashing_authorizations(customer_cashing_id) {
    return Vue.axios.get(`customer_cashings/${customer_cashing_id}/cancel_authorizations`, {})
  },

  capture_cashing_authorizations(customer_cashing_id) {
    return Vue.axios.get(`customer_cashings/${customer_cashing_id}/capture_authorizations`, {})
  },

  get_customer_invoices(customer_id, params) {
    return Vue.axios.get(`customers/${customer_id}/invoices`, { params })
  },

  get_agencies_with(params) {
    return Vue.axios.get(`agencies`, { params })
  },

  organizations_of_customer(customer_id) {
    return Vue.axios.get(`customers/${customer_id}/organizations`, {})
  },

  get_balance(customer_id, params) {
    return Vue.axios.get(`customers/${customer_id}/balance_state`, { params })
  },

  storeCustomerAddress(customerId, model) {
    return Vue.axios.post(`customers/${customerId}/addresses`, model)
  },

  fetchBookings(params, cancelToken) {
    return Vue.axios.get(`bookings`, { params, cancelToken })
  },

  fetchPunctualUnavailabiliy(params) {
    return  Vue.axios.get(`punctual_unavailabilities`, { params })
  },

  get_hotline_contracts(params) {
    return Vue.axios.get(`customers/${params.customer_id}/contracts`, params.params)
  },

  get_hotline_messages(params) {
    return Vue.axios.get(`hotline_messages`, params)
  },

  get_customer_hotline_messages(params) {
    return Vue.axios.get(`customers/${params.customer_id}/hotline_messages`, params.params)
  },

  get_customer_hotline_message(params) {
    return Vue.axios.get(`customers/${params.customer_id}/hotline_messages/${params.hotline_message_id}`, params.params)
  },

  post_hotline_message(params){
    return Vue.axios.post(`customers/${params.customer_id}/hotline_messages`, params.params)
  },

  put_hotline_message(params){
    return Vue.axios.put(`customers/${params.customer_id}/hotline_messages/${params.hotline_message_id}`, params.params)
  },

  archive_hotline_message(params){
    return Vue.axios.put(`customers/${params.customer_id}/hotline_messages/${params.hotline_message_id}/archive`, params.params)
  },

  unarchive_hotline_message(params){
    return Vue.axios.put(`customers/${params.customer_id}/hotline_messages/${params.hotline_message_id}/unarchive`, params.params)
  },

  hotline_search_customers(params)
  {
    return Vue.axios.get(`customers`, {params})
  },

  // crm / leads
  getCrmUserGroups(params) {
    return Vue.axios.get(`user_groups`, { params })
  },

  get_all_leads(params)
  {
    params = _.assign(params, {with:"interactions;customer;lead_offers.offer.distributed_product.agency;offers.distributed_product.agency;offers.distributed_product.product;creating_user;contacts;lead_distribution_methods.distribution_method"})
    return Vue.axios.get(`leads`, { params })
  },

  put_lead_offer_supplement(params)
  {
    return Vue.axios.put(`/leads/${params.lead_id}/update_offer_supplement`, params.body)
  },

  get_lead(lead_id)
  {
    return Vue.axios.get(`leads/${lead_id}`, {
      params:{
        with:"interactions.user;customer;lead_distribution_methods.distribution_method;lead_offers.offer.distributed_product.agency;offers.distributed_product.agency;offers.distributed_product.product;contacts;creating_user;last_updating_status_user"
      }
    })
  },

  add_lead(params)
  {
    return Vue.axios.post(`leads`, params)
  },

  put_lead(lead_id, params)
  {
    return Vue.axios.put(`leads/${lead_id}`, params)
  },

  put_lead_status(lead_id, params)
  {
    return Vue.axios.put(`leads/${lead_id}/update_status`, params)
  },

  put_lead_agency(lead_id, params)
  {
    return Vue.axios.put(`leads/${lead_id}/update_agency`, params)
  },

  updateLeadManager(lead_id, params) {
    return Vue.axios.put(`leads/${lead_id}/update_manager`, params)
  },

  delete_lead(lead_id)
  {
    return Vue.axios.delete(`leads/${lead_id}`)
  },
  // crm / leads.contacts
  get_all_contacts_of(lead_id)
  {
    return Vue.axios.get(`leads/${lead_id}/contacts`)
  },

  add_contact_to(lead_id, params)
  {
      return Vue.axios.post(`leads/${lead_id}/contacts`, params)
  },

  get_contact_creation_form_for(lead_id)
  {
    return Vue.axios.get(`leads/${lead_id}/contacts/create`)
  },

  get_contact_edition_form_for(lead_id, contact_id)
  {
    return Vue.axios.get(`leads/${lead_id}/contacts/${contact_id}/edit`)
  },

  update_contact_of(lead_id, contact_id, params)
  {
    return Vue.axios.put(`leads/${lead_id}/contacts/${contact_id}`, params)
  },

  delete_contact_of(lead_id, contact_id){
    return Vue.axios.delete(`leads/${lead_id}/contacts/${contact_id}`)
  },

  // crm / leads.customers
  attach_lead(lead_id, customer_id)
  {
    return Vue.axios.post(`leads/${lead_id}/customers`, {customer_id})
  },

  unattach_lead(lead_id)
  {
    return Vue.axios.delete(`leads/${lead_id}/customers/1`)
  },

  // crm / leads.interactions
  get_interactions(lead_id)
  {
    return Vue.axios.get(`leads/${lead_id}/interactions`, {params:{with:"medias"}})
  },

  post_interaction(lead_id, params)
  {
    return Vue.axios.post(`leads/${lead_id}/interactions`, {interaction:params.body.interaction, medias:params.medias})
  },

  delete_interaction(lead_id, lead_interaction_id)
  {
    return Vue.axios.delete(`leads/${lead_id}/interactions/${lead_interaction_id}`)
  },

  put_interaction(lead_id, lead_interaction_id, params){
    return Vue.axios.put(`leads/${lead_id}/interactions/${lead_interaction_id}`, params)
  },

  schedule_interaction(lead_id, params)
  {
    return Vue.axios.post(`leads/${lead_id}/interactions/schedule`, {interaction:params.body.interaction, medias:params.medias})
  },

  update_schedule_interaction(lead_id, lead_interaction_id, params)
  {
    return Vue.axios.put(`leads/${lead_id}/interactions/${lead_interaction_id}/update_scheduled`, params)
  },

  realize_schedule_interaction(lead_id, lead_interaction_id, params)
  {
    return Vue.axios.put(`/leads/${lead_id}/interactions/${lead_interaction_id}/realize_scheduled`, params)
  },


  // crm / leads.offers
  post_offer_to_lead(lead_id, params)
  {
    return Vue.axios.post(`leads/${lead_id}/offers`, params)
  },

  delete_offer_of_lead(lead_id, offer_id)
  {
    return Vue.axios.delete(`leads/${lead_id}/offers/${offer_id}`)
  },

  attachDistributionMethodToLead(lead_id, params)
  {
    return Vue.axios.post(`leads/${lead_id}/distribution_methods`, params)
  },

  detachDistributionMethodFromLead(lead_id, distribution_method_id)
  {
    return Vue.axios.delete(`leads/${lead_id}/distribution_methods/${distribution_method_id}`)
  },

  get_distribution_methods()
  {
    return Vue.axios.get(`/distribution_methods`)
  },

  // crm / agencies.medias
  get_medias(agency_id)
  {
    return Vue.axios.get(`agencies/${agency_id}/get_documents`)
  },

  // User groups
  getUserGroups(params) {
    return Vue.axios.get(`user_groups`, { params })
  },

  getNotifications(requestParams) {
    requestParams = {...requestParams, 'orderBy': 'created_at', 'sortedBy': 'desc'}
    return Vue.axios.get(`notifications`, { params: requestParams })
  },

  readNotification(notificationId) {
    return Vue.axios.get(`notifications/${notificationId}/read`)
  },

  unreadNotification(notificationId) {
    return Vue.axios.get(`notifications/${notificationId}/unread`)
  },

  getManualNotifications() {
    return Vue.axios.get(`manual_notifications`)
  },

  sendManualNotification(params) {
    return Vue.axios.get(`manual_notifications/${params.notificationId}/send`, { params: { 'customer_id': params.customerId } })
  },

  getCustomerManualNotifications(customerId) {
    return Vue.axios.get(`customers/${customerId}/notifications/manuals`, { params: {
      'orderBy': 'created_at',
      'sortedBy': 'desc'
    }})
  },

  getNotificationsTypes() {
    return Vue.axios.get(`notifications/types`)
  },

  // LOYALTY PROGRAM
  getLoyaltyPrograms () {
    return Vue.axios.get(`loyalty_programs`)
  },

  getNewSubscriptionInfos (params) {
    return Vue.axios.get(`customers/${params.customer_id}/loyalty_program_subscriptions/create`, {
        params: {
            loyalty_program_id: params.loyalty_program_id
        }
    })
  },

  storeNewLoyaltyProgramSubscription (params) {
    return Vue.axios.post(`customers/${params.customer_id}/loyalty_program_subscriptions`, {
        loyalty_program_id: params.loyalty_program_id,
        accept_cgv: params.accept_cgv
    })
  },

  getCustomersSubscribedLoyaltyProgram (params) {
    return Vue.axios.get(`loyalty_program_subscriptions`, {
        params
    })
  },

  getLoyaltyProgramSubscriptions (params) {
    return Vue.axios.get(`customers/${params.customer_id}/loyalty_program_subscriptions`, {
        params: {
            programs: params.programs,
            statuses: params.statuses,
            with: 'program;promo_codes;activity_log.user;activity_log.suborder.suborderable'
        }
    })
  },

  terminateCustomerLoyaltyProgramSubscription (params) {
      return Vue.axios.put(`customers/${params.customerId}/loyalty_program_subscriptions/${params.lpsId}/terminate`)
  },

  disableCustomerLoyaltyProgramSubscription (params) {
      return Vue.axios.put(`customers/${params.customerId}/loyalty_program_subscriptions/${params.lpsId}/disable`)
  },

  enableCustomerLoyaltyProgramSubscription (params) {
      return Vue.axios.put(`customers/${params.customerId}/loyalty_program_subscriptions/${params.lpsId}/enable`)
  },

  updateCustomerLoyaltyProgramSubscription (params) {
      return Vue.axios.put(`customers/${params.customerId}/loyalty_program_subscriptions/${params.lpsId}/update_points_count`, {
        points_count: params.newPointsCount,
        confirm: params.pointsCountConfirm,
        comments: params.pointsCountComment
      })
  },

  // Beneficial owners
  getBeneficialOwnersDeclaration(customerId, params) {
    return Vue.axios.get(`customers/${customerId}/beneficial_owners_declaration`, { params })
  },
  updateBeneficialOwnersDeclaration(customerId, params) {
    return Vue.axios.put(`customers/${customerId}/beneficial_owners_declaration`, params)
  },
  getInpiDocument(declarationId, params) {
    return Vue.axios.get(`beneficial_owners_declarations/${declarationId}/inpi_rbe_documents`, { params })
  },
  updateInpiDocument(declarationId, params) {
    return Vue.axios.put(`beneficial_owners_declarations/${declarationId}/inpi_rbe_documents/${params.id}`, params)
  },
  getBeneficialOwners(declarationId, params) {
    return Vue.axios.get(`beneficial_owners_declarations/${declarationId}/beneficial_owners`, { params })
  },
  getBeneficialOwnerCreationForm(declarationId, params) {
    return Vue.axios.get(`beneficial_owners_declarations/${declarationId}/beneficial_owners/create`, { params })
  },
  storeBeneficialOwner(declarationId, params) {
    return Vue.axios.post(`beneficial_owners_declarations/${declarationId}/beneficial_owners`, params)
  },
  getBeneficialOwnerEditionForm(declarationId, ownerId, params) {
    return Vue.axios.get(`beneficial_owners_declarations/${declarationId}/beneficial_owners/${ownerId}/edit`, { params })
  },
  updateBeneficialOwner(declarationId, ownerId, params) {
    return Vue.axios.put(`beneficial_owners_declarations/${declarationId}/beneficial_owners/${ownerId}`, params)
  },
  deleteBeneficialOwner(declarationId, params) {
    return Vue.axios.delete(`beneficial_owners_declarations/${declarationId}/beneficial_owners/${params.id}`)
  },
  
  // Social certificate
  getCustomerSocialCertificate(customerId) {
    return Vue.axios.get(`customers/${customerId}/social_certificate`)
  },
  
  // getCustomerSocialCertificateEditionForm(customerId) {
  //   return Vue.axios.get(`customers/${customerId}/social_certificate/edit`)
  // },
  
  getCustomerSocialCertificateEditionSchema(customerId, step) {
    return Vue.axios.get(`customers/${customerId}/social_certificate/edition_schema?step=${step}`)
  },

  updateCustomerSocialCertificate(customerId, model) {
    return Vue.axios.put(`customers/${customerId}/social_certificate/update`, model)
  },

  fetchSettings(params) {
    return Vue.axios.get('settings', {params})
  }
}

const apiV2 = {
  getValidationRequests(params) {
    return Vue.axios.request({
      method: 'get',
      url: 'validation_requests',
      baseURL: `${__API_URL__}/api/v2`,
      params,
      paramsSerializer: function (params) {
        return qs.stringify(params, {arrayFormat: 'brackets'})
      }
    })
  },

  getValidationRequest(validationRequestid, params) {
    return Vue.axios.request({
      method: 'get',
      url: `validation_requests/${validationRequestid}`,
      baseURL: `${__API_URL__}/api/v2`,
      params,
      paramsSerializer: function (params) {
        return qs.stringify(params, {arrayFormat: 'brackets'})
      }
    })
  },

  getCustomerValidationRequests(customerId, params) {
    return Vue.axios.request({
      method: 'get',
      url: `customers/${customerId}/validation_requests`,
      baseURL: `${__API_URL__}/api/v2`,
      params,
      paramsSerializer: function (params) {
        return qs.stringify(params, {arrayFormat: 'brackets'})
      }
    })
  },

  fetchCustomers(params) {
    return Vue.axios.request({
      method: 'get',
      url: 'customers',
      baseURL: `${__API_URL__}/api/v2`,
      params: { ...params, paginate: 0 },
      paramsSerializer: function (params) {
        return qs.stringify(params, {arrayFormat: 'brackets'})
      }
    })
  },

  getCustomer(customerId) {
    return Vue.axios.request({
      method: 'get',
      url: `customers/${customerId}`,
      baseURL: `${__API_URL__}/api/v2`,
    })
  },
}

export default api
export { apiV2 }



// WEBPACK FOOTER //
// ./client/store/api.js